<template>
<v-dialog
    v-model="dialog"
    fullscreen
    v-if="contact != null"
>
    <v-card>
        <v-toolbar
            dark
            color="primary"
        >
            <v-toolbar-title>{{dialogTitle()}}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
                <v-btn
                    text
                    dark
                    v-if="canDelete()"
                    @click="onDeleteContact"
                >
                    Delete
                </v-btn>
                <v-btn
                    v-model="show_more_fields"
                    dark
                    text
                    @click="show_more_fields = !show_more_fields"
                >
                    Show More Info
                </v-btn>

                <v-btn
                    dark
                    text
                    @click="onClickDiscardChanges"
                >
                    Cancel
                </v-btn>

                <v-btn
                    dark
                    text
                    @click="onClickSave"
                    :disabled="!is_valid"
                >
                    {{dialogSaveText()}}
                </v-btn>
            </v-toolbar-items>
        </v-toolbar>

        <v-card-text>
            <v-form v-model="is_valid">

            <!------------------------------------- NAME ------------------------------------------------->
            <m-contact-line
                class="mt-4"
                left_icon="mdi-account"
                label="Prefix" 
                v-model="contact.name.prefix"
                v-show="show_more_fields"
                v-on:input="show_warning = true"
            />

            <m-contact-line
                class="mt-6"
                :left_icon="show_more_fields ? 'mdi-blank' : 'mdi-account'" 
                label="First Name" 
                v-model="contact.name.first"
                v-on:input="show_warning = true"
            />

            <m-contact-line 
                label="Middle Name"
                v-model="contact.name.middle"
                v-show="show_more_fields"
                v-on:input="show_warning = true"
            />

            <m-contact-line 
                label="Last Name" 
                v-model="contact.name.last"
                :rules="[rules.required]"
                v-on:input="show_warning = true"
            />

            <m-contact-line 
                label="Suffix" 
                v-model="contact.name.suffix"
                v-show="show_more_fields"
                v-on:input="show_warning = true"
            />

            <m-contact-line 
                label="Phonetic" 
                v-model="contact.name.phonetic"
                v-show="show_more_fields"
                v-on:input="show_warning = true"
            />

            <m-contact-line 
                label="Nickname" 
                v-model="contact.name.nickname"
                v-show="show_more_fields"
                v-on:input="show_warning = true"
            />
            
            <m-contact-line 
                label="Label As" 
                hint="If filled out, the contact will show this label throughout the system"
                v-model="contact.name.explicit_label"
                v-show="show_more_fields || contact.name.explicit_label != null"
                v-on:input="show_warning = true"
            />

            <!------------------------------------- EMPLOYMENT SUMMARY ------------------------------------------------->
            <m-contact-line 
                left_icon="mdi-domain"
                label="Company" 
                v-model="contact.employment_summary.organization_name"
            />

            <m-contact-line 
                label="Job Title" 
                v-model="contact.employment_summary.job_title"
            />

            <m-contact-line 
                label="Department" 
                v-model="contact.employment_summary.department"
                v-show="show_more_fields"
            />

            <!-------------------------------------- PHONE NUMBERS -------------------------------------------------->

            <div
                v-for="(phone, index) in phone_numbers_manual"
                :key="index+'_phone'"
            >
              <m-contact-line
                  left_icon="mdi-phone"
                  label="Phone Number"
                  v-model="phone.phone_number"
                  phone_number
                  :show_delete="!isOnlyPhoneNumber(index)"
                  :required="isOnlyPhoneNumber(index) && !skip_phone_requirement"
                  :show_add="isLastPhoneManual(index)"
                  v-on:on-delete="deletePhoneManual(index)"
                  v-on:on-add="addPhoneManual()"
              />

              <m-contact-line
                  label="Label"
                  v-model="phone.label"
              />
            </div>

            <div
                v-if="phone_numbers_redchirp.length > 0"
              >
              <v-row
                  dense
                  align-content-lg="space-around"
                  v-on:click="showRedChirpPhoneHelpText()"
              >
                <v-subheader>
                  RedChirp Phone Numbers
                </v-subheader>
                <v-icon
                    slot="append"
                >
                  mdi-information-outline
                </v-icon>
              </v-row>

              <div
                  v-for="(phone, index) in phone_numbers_redchirp"
                  :key="index+'_phone_redchirp'"
                  v-on:click="showRedChirpPhoneHelpText()"
              >
                <m-contact-line
                    left_icon="mdi-phone"
                    label="Phone Number"
                    v-model="phone.phone_number"
                    phone_number
                    disabled
                    show_delete
                />
                <m-contact-line
                    label="Label"
                    v-model="phone.label"
                    :disabled="isPhoneDisabled(index,phone)"
                />
              </div>
            </div>

            <div
                v-if="phone_numbers_personal.length > 0"
            >
              <v-row
                  dense
                  align-content-lg="space-around"
                  v-on:click="showPersonalPhoneHelpText()"
              >
                <v-subheader>
                  Personal Phone Numbers
                </v-subheader>
                <v-icon
                    slot="append"
                >
                  mdi-information-outline
                </v-icon>
              </v-row>

              <div
                  v-for="(phone, index) in phone_numbers_personal"
                  :key="index+'_phone_personal'"
                  v-on:click="showPersonalPhoneHelpText()"
              >
                <m-contact-line
                    left_icon="mdi-phone"
                    label="Phone Number"
                    v-model="phone.phone_number"
                    phone_number
                    disabled
                    show_delete
                />
                <m-contact-line
                    label="Label"
                    v-model="phone.label"
                    :disabled="isPhoneDisabled(index,phone)"
                />
              </div>
            </div>

            <!-------------------------------------- EMAIL ADDRESSES -------------------------------------------------->
            <div
                v-for="(email, index) in contact.email_addresses"
                :key="index+'_email'"
            >
                <m-contact-line 
                    left_icon="mdi-email"
                    label="Email Address" 
                    v-model="email.email"
                    email
                    :show_add="isLastEmail(index)"
                    show_delete
                    v-on:on-delete="deleteEmail(index)"
                    v-on:on-add="addEmail()"
                />

                <m-contact-line 
                    label="Label" 
                    v-model="email.label"
                />
            </div>

            <!-------------------------------------- POSTAL ADDRESSES -------------------------------------------------->

            <div
                v-for="(address, index) in contact.postal_addresses"
                :key="index+'_postal_address'"
                v-show="show_more_fields"
            >
                <m-contact-line 
                    left_icon="mdi-map-marker"
                    label="Address Label" 
                    hint="e.g. Home, Office, Warehouse, etc."
                    v-model="address.label"
                    :show_add="isLastAddress(index)"
                    show_delete
                    v-on:on-delete="deleteAddress(index)"
                    v-on:on-add="addAddress()"
                />

                <m-contact-line label="Address Line 1" v-model="address.address.address_line1"/>
                <m-contact-line label="Address Line 2" v-model="address.address.address_line2"/>
                <m-contact-line label="Address Line 3" v-model="address.address.address_line3"/>
                <m-contact-line label="City" v-model="address.address.city"/>
                <m-contact-line label="State" v-model="address.address.state"/>
                <m-contact-line label="Zip Code" v-model="address.address.postal_code"/>
                <m-contact-line label="Country Code" v-model="address.address.country"/>

            </div>

            <!-------------------------------------- EXTENDED FIELDS -------------------------------------------------->
            <m-contact-line 
                left_icon="mdi-cake-variant"
                label="Birthday" 
                hint="(MM/DD or MM/DD/YYYY) - e.g. 2/8 for Feb 8th, 2/8/1981 for Feb 8th 1981"
                us_date_with_optional_year
                v-model="contact.birthday"
                v-show="show_more_fields"
            />

            <!-------------------------------------- OTHER EVENTS -------------------------------------------------->

            <div
                v-for="(event, index) in contact.events"
                :key="index+'_events'"
                v-show="show_more_fields"
            >
                <m-contact-line 
                    left_icon="mdi-calendar"
                    label="Event Date" 
                    v-model="event.date"
                    us_date_with_optional_year
                    :show_add="isLastEvent(index)"
                    show_delete
                    v-on:on-delete="deleteEvent(index)"
                    v-on:on-add="addEvent()"
                />

                <m-contact-line 
                    label="Label" 
                    v-model="event.label"
                />
            </div>

            <!-------------------------------------- MORE EXTENDED FIELDS -------------------------------------------------->

            <m-contact-line 
                left_icon="mdi-link"
                label="Website" 
                v-model="contact.website"
                v-show="show_more_fields"
            />

            <m-contact-line 
                left_icon="mdi-cog"
                label="Custom Field" 
                v-model="contact.custom_field"
                v-show="show_more_fields"
            />

            <m-contact-line 
                left_icon="mdi-file"
                label="Notes" 
                v-model="contact.notes"
            />
            </v-form>

            <v-card-text
                v-if="$rc.$state.isSystemsAdmin() && !admin_show_contact_details"
            >
                <a @click="onAdminShowDetails()">Staff Only: view contact details (json)</a>
            </v-card-text>

            <m-code-block
                v-if="admin_show_contact_details"
            >
                {{contact_details}}
            </m-code-block>

        </v-card-text>
    </v-card>
  
</v-dialog>
</template>

<script>

const E164 = require('@/js/E164.js');
const { v4: uuidv4 } = require('uuid');
const _ = require('lodash');

const empty_postal_address = {
                "address" : {
                    "address_line1" : null,
                    "address_line2" : null,
                    "address_line3" : null,
                    "city" : null,
                    "state" : null,
                    "postal_code" : null,
                    "country" : "US"
                },
                "label" : null,
            };

const empty_event = {
    date: null,
    label: null,
};

const empty_email_address = {
    email: null,
    label: null,
};

const empty_phone_number = {
    phone_number: null,
    label: null,
    contact_phone_number_source: 'manual-from-contacts',
};

function isNotBlank(value)
{
    if ( value == null ) return false;
    if ( value.trim() === '' ) return false;
    return true;
}

export default {

    props: {
        organization_id: {
            type: String,
            default: function() { return ''; }
        },
    },

    data() {
        return {
          dialog: false,
          is_valid: true,
          contact: null,
          show_more_fields: false,
          show_warning : false,
          is_edit: false,
          phone_numbers_manual: null,
          phone_numbers_redchirp: null,
          phone_numbers_personal: null,
          skip_phone_requirement: false,
          onContactUpdate: null,
          admin_show_contact_details: false,

          rules: {
                required: (value) =>
                {
                    if ( value == null || value.trim().length == 0 )
                    {
                        return 'Required';
                    }

                    return true;
                },
            },
        }
    },

    methods: {

        onAdminShowDetails()
        {
            this.admin_show_contact_details = true;
        },

        showRedChirpPhoneHelpText()
        {
          this.$rc.alert('RedChirp Phone Numbers', 'RedChirp Phone numbers are assigned by your Organization ' +
              'and cannot be modified here.');
        },

        showPersonalPhoneHelpText()
        {
          this.$rc.alert('Personal Phone Numbers','Personal Phone numbers can be modified in the Account Settings page.');
        },

        canDelete()
        {
            let actor_permissions = this.$rc.$state.profile.actor_permissions;
            if (actor_permissions === null) return false;

            // don't delete contact information for employee accounts
            if (this.contact && this.contact.of_employee_id && this.contact.of_employee_id.length > 0) return false;

            return true;
        },

        dialogTitle() {
            if (this.is_edit)
            {
                return "Edit Contact";
            }

            return "Create Contact";
        },

        dialogSaveText() {
            if (this.is_edit)
            {
                return "Save";
            }

            return "Create";
        },

        splitPhones() {
          this.phone_numbers_manual = [];
          this.phone_numbers_redchirp = [];
          this.phone_numbers_personal = [];

          for (let i = 0; i < this.contact.phone_numbers.length; i++)
          {
            let phone_number = this.contact.phone_numbers[i];

            switch (phone_number.contact_phone_number_source)
            {
              case "redchirp-phone-number":
              {
                this.phone_numbers_redchirp.push(phone_number);
                break;
              }
              case "personal-phone-number":
              {
                this.phone_numbers_personal.push(phone_number);
                break;
              }
              case "manual-from-contacts":
              default:
              {
                this.phone_numbers_manual.push(phone_number);
                break;
              }
            }
          }
        },

        createDefaultContact()
        {
            return {
                "contact_id" : uuidv4(),
                "organization_id" : this.organization_id,
                "avatar_image" : null,
                "images" : [ ],
                "name" : {
                    "prefix" : null,
                    "first" : null,
                    "middle" : null,
                    "last" : null,
                    "suffix" : null,
                    "phonetic" : null,
                    "nickname" : null,
                    "explicit_label" : null
                },
                "employment_summary" : {
                    "organization_name" : null,
                    "job_title" : null,
                    "department" : null
                },
                "birthday" : null,
                "phone_numbers" : [{ "phone_number" : null, "label" : null, "contact_phone_number_source" : 'manual-from-contacts' }],
                "email_addresses" : [ { "email" : null, "label" : null}],
                "postal_addresses" : [ ],
                "events" : [ ],
                "website" : null,
                "notes" : null,
                "custom_field" : null,
                "time_zone" : null,
                "of_employee_id" : null
            };
        },

        showCreateDialog(contact,is_edit,skip_phone_requirement,onContactUpdate)
        {
            this.admin_show_contact_details = false;

            if (contact == null)
            {
                this.contact =  this.createDefaultContact();
            }
            else
            {
                this.contact = contact;
            }

            this.addBlankAddress();
            this.addBlankEvent();
            this.addBlankEmail();

            // The ordering needs to be exact. Pretty print all phone numbers, split them, then add blank if needed
            this.prettyPrintAllPhoneNumbers();
            this.splitPhones();
            this.addBlankPhoneNumber();

            this.dialog = true;

            this.is_edit = is_edit;
            this.dialog = true;
            this.skip_phone_requirement = skip_phone_requirement;
            this.onContactUpdate = onContactUpdate;
            this.$router.dialog_guard = this.dialogGuard;
       },

        addBlankAddress()
        {
            if ( this.contact.postal_addresses.length != 0 )
                return;

            this.contact.postal_addresses.push(_.cloneDeep(empty_postal_address));
        },

        addBlankEmail()
        {
            if ( this.contact.email_addresses.length != 0 )
                return;

            this.contact.email_addresses.push(_.cloneDeep(empty_email_address));
        },

        addBlankPhoneNumber()
        {
            if ( this.phone_numbers_manual.length != 0 )
                return;

            this.phone_numbers_manual.push(_.cloneDeep(empty_phone_number));
        },

        addBlankEvent()
        {
            if ( this.contact.events.length != 0 )
                return;

            this.contact.events.push(_.cloneDeep(empty_event));
        },

        removeBlankAddresses()
        {
            for ( let i = this.contact.postal_addresses.length-1; i >= 0; i-- )
            {
                let address = this.contact.postal_addresses[i].address;

                if ( isNotBlank(address.address_line1) ) continue;
                if ( isNotBlank(address.address_line2) ) continue;
                if ( isNotBlank(address.address_line3) ) continue;
                if ( isNotBlank(address.city) ) continue;
                if ( isNotBlank(address.state) ) continue;
                if ( isNotBlank(address.postal_code) ) continue;
                if ( isNotBlank(address.country) && address.country != 'US' ) continue;

                this.contact.postal_addresses.splice(i,1);
            }
        },

        removeBlankEvents()
        {
            for ( let i = this.contact.events.length-1; i >= 0; i-- )
            {
                let event = this.contact.events[i];

                if ( isNotBlank(event.date) ) continue;
                if ( isNotBlank(event.label) ) continue;

                this.contact.events.splice(i,1);
            }
        },

        removeBlankEmails()
        {
            for ( let i = this.contact.email_addresses.length-1; i >= 0; i-- )
            {
                let email = this.contact.email_addresses[i];

                if ( isNotBlank(email.email) ) continue;
                if ( isNotBlank(email.label) ) continue;

                this.contact.email_addresses.splice(i,1);
            }
        },

        consolidatePhoneNumbers()
        {
          this.contact.phone_numbers = [];

          // since most primary phone numbers are redchirp numbers, push those in first
          for (let i = 0; i < this.phone_numbers_redchirp.length; i++)
          {
            this.contact.phone_numbers.push(this.phone_numbers_redchirp[i]);
          }

          for (let i = 0; i < this.phone_numbers_manual.length; i++)
          {
            this.contact.phone_numbers.push(this.phone_numbers_manual[i]);
          }

          for (let i = 0; i < this.phone_numbers_personal.length; i++)
          {
            this.contact.phone_numbers.push(this.phone_numbers_personal[i]);
          }
        },

        removeBlankPhoneNumbers()
        {
            for ( let i = this.contact.phone_numbers.length-1; i >= 0; i-- )
            {
                let phone_number = this.contact.phone_numbers[i];

                if ( isNotBlank(phone_number.phone_number) ) continue;
                if ( isNotBlank(phone_number.label) ) continue;

                this.contact.phone_numbers.splice(i,1);
            }
        },

        prettyPrintAllPhoneNumbers()
        {
            for ( let i = 0; i < this.contact.phone_numbers.length; i++ )
            {
                this.contact.phone_numbers[i].phone_number = E164.prettyPrintUsPhoneNumber(this.contact.phone_numbers[i].phone_number);
            }
        },

        normalizeAllPhoneNumbers()
        {
            for ( let i = 0; i < this.contact.phone_numbers.length; i++ )
            {
                let raw_value = this.contact.phone_numbers[i].phone_number;
                let normal_value = E164.createEndpointFromUSPhoneNumber(raw_value,null);

                if ( normal_value == null )
                    continue;

                normal_value = normal_value.substring("E164:".length);
                this.contact.phone_numbers[i].phone_number = normal_value;
            }
        },

        normalizePhoneNumber(raw_value)
        {
            let normal_value = E164.createEndpointFromUSPhoneNumber(raw_value,null);
            if ( normal_value == null ) return raw_value;
            return normal_value;
        },

        isLastEmail(index)
        {
            return index == this.contact.email_addresses.length - 1;
        },

        deleteEmail(index)
        {
            if ( index == 0 && this.contact.email_addresses.length == 1 )
            {
                this.contact.email_addresses[0].email = null;
                this.contact.email_addresses[0].label = null;
                return;
            }

            this.contact.email_addresses.splice(index, 1);
        },

        addEmail()
        {
            this.contact.email_addresses.push({
                'email' : null,
                'label' : null,
            })
        },

        isLastPhoneManual(index)
        {
            return index == this.phone_numbers_manual.length-1;
        },

        isOnlyPhoneNumber()
        {
          // check all phone sections. Only enforce the phone number if they don't have redchirp or personal
          if (this.phone_numbers_personal.length == 0 && this.phone_numbers_redchirp.length == 0
              && this.phone_numbers_manual.length == 1)
          {
            return true;
          }

          return false;
        },

        deletePhoneManual(index)
        {
            if ( index == 0 && this.phone_numbers_manual.length == 1 )
            {
                this.phone_numbers_manual[0].phone_number = null;
                this.phone_numbers_manual[0].label = null;
                this.phone_numbers_manual[0].contact_phone_number_source = 'manual-from-contacts';
                return;
            }

            this.phone_numbers_manual.splice(index,1);
        },

        isPhoneDisabled(index,phone)
        {
            if (phone.contact_phone_number_source != null && phone.contact_phone_number_source != 'manual-from-contacts')
            {
              return true;
            }

            // if here, it's the manual contacts section. If employee, they can only delete if they still have a phone
            // from a different section
            if ( this.contact.of_employee_id != null )
            {
              if (this.phone_numbers_manual.length == 1
                  && this.phone_numbers_redchirp.length == 0 && this.phone_numbers_personal.length == 0)
              {
                return true;
              }
            }

            return false;
        },

        addPhoneManual()
        {
          this.phone_numbers_manual.push(_.cloneDeep(empty_phone_number));
        },

        isLastAddress(index)
        {
            return index == this.contact.postal_addresses.length - 1;
        },

        deleteAddress(index)
        {
            if ( index == 0 && this.contact.postal_addresses.length == 1 )
            {
                this.contact.postal_addresses[0] = _.cloneDeep(empty_postal_address);
                this.$forceUpdate();
                return;
            }

            this.contact.postal_addresses.splice(index, 1);
        },

        addAddress()
        {
            this.contact.postal_addresses.push(_.cloneDeep(empty_postal_address))
        },

        isLastEvent(index)
        {
            return index == this.contact.events.length - 1;
        },

        deleteEvent(index)
        {
            if ( index == 0 && this.contact.events.length == 1 )
            {
                this.contact.events[0] = _.cloneDeep(empty_event);
                this.$forceUpdate();
                return;
            }

            this.contact.events.splice(index, 1);
        },

        addEvent()
        {
            this.contact.events.push(_.cloneDeep(empty_event))
        },

        closeDialog()
        {
            this.dialog = false;
            this.$router.dialog_guard = null;
        },

        dialogGuard(to, from, next)
        {
            if ( this.dialog == false)
            {
                // slipup somehow
                this.$router.dialog_guard = null;
                return;
            }
        
            // The dialog is showing...
            if ( this.is_valid )
            {
                // data is valid, so save 
                this.save();
                this.dialog = false;
                next(false);
                return;
            }
        
            this.$rc.snackbar('You must either correct the errors or discard your changes.',3500);
            this.dialog = true;
            next(false);
        },

        onClickSave()
        {
            if ( this.show_warning && this.contact && this.contact.of_employee_id )
            {
                this.$rc.confirmWithResponse(
                    'Confirm Name Change',
                    'Only change a RedChirp User\'s name if the individual\'s name has actually changed; common examples include marriage or correcting a typo. If you are replacing a team member with a new hire, you should archive this user and create a new user instead. \n' +
                    'Changing a user\'s name will change all labels of all messages they have previously sent.\n',
                    'confirm',
                    [
                        {'text': 'Cancel', 'value': 'cancel'},
                        {'text': 'Confirm', 'value': 'confirm', 'disable_until_valid' : true },
                    ],

                    (response) => {
                        if (response == 'confirm')
                        {
                            this.save();
                            this.closeDialog();
                            this.$emit('update');
                        }
                        else
                        {
                            return;
                        }
                    }
                );

                return;
            }

            this.save();
            this.closeDialog();
            this.$emit('update');
        },

        onClickDiscardChanges()
        {
            this.closeDialog();
        },

        onDeleteContact()
        {
            this.$rc.confirm(
                'Delete Contact',
                'This will delete the contact from the system and cannot be recovered.',
                [
                    {'text' : 'Cancel', 'value' : 'cancel' },
                    {'text' : 'Delete', 'value' : 'confirmed' },
                ],

                (response) => {
                    if ( response != 'confirmed' ) return;
                    
                    this.onDeleteImpl();
                }
            );
        },

        onDeleteImpl()
        {
            this.$rc.showSpinner();
            this.$rc.deleteContact(
                this.contact,
                (data) => {
                    this.$rc.hideSpinner();
                    this.closeDialog();
                    this.$emit('update');
                },
            
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to delete contact');
                },
            );
        },

        save()
        {
            this.$rc.showSpinner();
            this.removeBlankAddresses();
            this.removeBlankEvents();
            this.removeBlankEmails();

            // the following operations need to happen in sequence (consolidate phone numbers before
            // any cleanup happens)
            this.consolidatePhoneNumbers();
            this.removeBlankPhoneNumbers();
            this.normalizeAllPhoneNumbers();

            if (this.skip_phone_requirement)
            {
              this.contact.skip_phone_requirement = true;
            }

            if (this.onContactUpdate)
            {
              this.onContactUpdate(this.contact);
            }
            else
            {
              this.$rc.upsertContact(
                  this.contact,

                  (data) => {
                    this.$rc.hideSpinner();
                  },

                  () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to create contact');
                  },
              );
            }
        },
    },
    computed: {
    
        contact_details()
        {
            return JSON.stringify(this.contact,null,2);  
        },
    }
}
</script>

<style lang="scss" scoped>
</style>